import { useNavigate } from 'react-router-dom';
import { Badge } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, StatusBadge, QueryState, StatCard, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import DataTable from '../components/DataTable';
import { formatDateTime } from '../lib/format';

const JOB = { succeeded: 'success', warning: 'warning', failed: 'error', running: 'primary', never: 'default' };

/**
 * Infrastructure: custom and platform domains (DNS verification; TLS ends at the edge
 * proxy), merchant database probes, provisioning jobs and the sweep's background jobs
 * (job runs recorded through @dreamtree-org/jobs). Everything is read from the platform DB.
 */
export default function Infrastructure() {
  const nav = useNavigate();
  const query = useQuery('platformInfrastructure', undefined, { select: (r) => r.data });

  return (
    <div className="space-y-6">
      <PageHeader title="Infrastructure" subtitle="Domains, isolated databases, provisioning and background jobs" />
      <QueryState query={query}>
        {(d) => {
          const custom = d.domains.filter((x) => x.kind === 'custom');
          const attention = d.background_jobs.filter((j) => ['failed', 'warning', 'never'].includes(j.status));
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Domains verified" value={`${d.domains.filter((x) => x.domain_status === 'verified').length} / ${d.domains.length}`} hint={`${custom.length} custom`} />
                <StatCard label="Databases healthy" value={`${d.databases.filter((x) => x.health === 'healthy').length} / ${d.databases.length}`} hint="last sweep probe" />
                <StatCard label="Provisioning failures" value={d.provisioning.filter((j) => j.status === 'failed').length} hint="retry from the merchant page" />
                <StatCard label="Jobs needing attention" value={attention.length} hint="failed in 24 h or never ran" />
              </div>

              <SectionCard title="Domains & TLS">
                <DataTable
                  data={d.domains}
                  columns={[
                    { key: 'hostname', label: 'Hostname', render: (c) => <span className="font-medium break-all">{c.hostname}</span> },
                    { key: 'merchant_slug', label: 'Merchant', hideOnMobile: true },
                    { key: 'kind', label: 'Kind', hideOnMobile: true, render: (c) => <Badge variant={c.kind === 'custom' ? 'primary' : 'outline'} size="sm">{c.kind}</Badge> },
                    { key: 'domain_status', label: 'Domain', render: (c) => <StatusBadge status={c.domain_status} /> },
                    { key: 'tls', label: 'TLS', hideOnMobile: true, wrap: true },
                    { key: 'dns', label: 'DNS check', hideOnMobile: true, wrap: true, render: (c) => (c.verified_at ? `verified ${formatDateTime(c.verified_at)}` : c.dns) },
                  ]}
                />
                <p className="text-xs opacity-60">TLS certificates are issued at the edge proxy, not by this app; a custom domain gets one once its DNS TXT record verifies.</p>
              </SectionCard>

              <SectionCard title="Merchant databases">
                <DataTable
                  data={d.databases}
                  onRowClick={(x) => nav(`/merchants/${x.merchant_id}?tab=database`)}
                  columns={[
                    { key: 'merchant', label: 'Merchant', render: (x) => (
                      <div><div className="font-medium">{x.merchant}</div><code className="text-xs opacity-70">{x.database_name}</code></div>
                    ) },
                    { key: 'connection', label: 'Connection', hideOnMobile: true },
                    { key: 'health', label: 'Health', render: (x) => <StatusBadge status={x.health} /> },
                    { key: 'size_mb', label: 'Size', hideOnMobile: true, render: (x) => (x.size_mb == null ? '—' : `${x.size_mb} MB`) },
                    { key: 'probe_ms', label: 'Round trip', render: (x) => (x.probe_ms == null ? '—' : x.probe_ms < 1 ? '< 1 ms' : `${x.probe_ms} ms`) },
                    { key: 'sampled_at', label: 'Probed', hideOnMobile: true, render: (x) => formatDateTime(x.sampled_at) },
                  ]}
                />
              </SectionCard>

              <div className="grid gap-4 2xl:grid-cols-2">
                <SectionCard title="Provisioning jobs">
                  {d.provisioning.length ? (
                    <DataTable
                      data={d.provisioning}
                      columns={[
                        { key: 'merchant_slug', label: 'Merchant', render: (j) => <span className="font-medium">{j.merchant_slug}</span> },
                        { key: 'operation', label: 'Operation' },
                        { key: 'status', label: 'Status', render: (j) => <StatusBadge status={j.status} /> },
                        { key: 'attempts', label: 'Attempts', hideOnMobile: true, render: (j) => `${j.attempts}/${j.max_attempts}` },
                        { key: 'at', label: 'When', render: (j) => formatDateTime(j.at) },
                      ]}
                    />
                  ) : <EmptyState title="No provisioning jobs yet" />}
                </SectionCard>
                <SectionCard title="Background jobs · last 24 h">
                  <DataTable
                    data={d.background_jobs}
                    columns={[
                      { key: 'label', label: 'Job', wrap: true, render: (j) => (
                        <div>
                          <div className="font-medium">{j.label}</div>
                          <div className="text-xs opacity-60">{j.schedule}</div>
                          {j.last_error ? <div className="text-xs text-error-600 break-all">{j.last_error}</div> : null}
                        </div>
                      ) },
                      { key: 'status', label: 'Status', render: (j) => <Badge variant={JOB[j.status] || 'default'} size="sm">{j.status === 'never' ? 'not run' : j.status}</Badge> },
                      { key: 'runs_24h', label: 'Runs', hideOnMobile: true, render: (j) => `${j.runs_24h}${j.failures_24h ? ` (${j.failures_24h} failed)` : ''}` },
                      { key: 'last_run_at', label: 'Last run', hideOnMobile: true, render: (j) => formatDateTime(j.last_run_at) },
                    ]}
                  />
                </SectionCard>
              </div>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
